import moment from 'moment';
import { PastContact } from '../../../api/PastContacts/schema';
import { Ownership, PastContactsTableSortColumn } from '../PastContactsTypes';
import { SortDirection } from '../../../Types/SortDirection';
import { DURATION_OPTIONS, TIME_OF_DAY_OPTIONS } from './PastContactsConstants';

export const getOwnershipColour = (
  resolution: Ownership
): 'success' | 'warning' | 'neutral' | 'theme' | 'error' => {
  switch (resolution) {
    case Ownership.RESOLVED:
      return 'success';
    case Ownership.PARKED:
      return 'warning';
    case Ownership.MARKED_SPAM:
      return 'error';
    case Ownership.MARKED_DUPLICATE:
      return 'warning';
    case Ownership.TRANSFERED:
      return 'theme';
    case Ownership.CHAT_DROPPED:
      return 'neutral';
    case Ownership.CONSULTED:
      return 'warning';
    case Ownership.UNKNOWN:
      return 'neutral';
    default:
      return 'neutral';
  }
};

export const formatDateTime = (isoTimestamp: string): string => {
  try {
    const momentDate = moment(isoTimestamp);

    if (!momentDate.isValid()) {
      return isoTimestamp;
    }

    // Format as "YYYY-MM-DD    h:mm A" with 4 spaces between date and time
    return momentDate.format('YYYY-MM-DD    h:mm A');
  } catch (error) {
    // Return original value if parsing fails
    return isoTimestamp;
  }
};

export const formatDuration = (
  duration: string | number | undefined
): string => {
  try {
    // Handle undefined case
    if (duration === undefined || duration === null) {
      return '--';
    }

    // Convert to number if it's a string
    const totalSeconds =
      typeof duration === 'string' ? parseInt(duration, 10) : duration;

    if (isNaN(totalSeconds) || totalSeconds < 0) {
      return duration.toString();
    }

    // Calculate minutes and seconds
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    // Format as M:SSmin (e.g., 1:12min)
    return `${minutes}:${seconds.toString().padStart(2, '0')}min`;
  } catch (error) {
    // Return fallback value if parsing fails
    return '--';
  }
};

export const filterContacts = (
  preFilteredContacts: PastContact[],
  searchTerm: string | undefined,
  selectedChannel: string,
  selectedTimeOfDay: number,
  selectedDuration: number,
  selectedOwnership: Ownership
): PastContact[] => {
  if (!preFilteredContacts || !Array.isArray(preFilteredContacts)) {
    return [];
  }

  return preFilteredContacts.filter(contact => {
    // Channel filter
    if (selectedChannel !== 'All') {
      // Use CHANNEL_NAME property from the contact
      const contactChannel = contact.CHANNEL_NAME ?? 'Unknown';
      if (selectedChannel !== contactChannel) {
        return false;
      }
    }

    if (selectedDuration !== 0 && contact.CALL_DURATION_SECONDS) {
      const durationOption = DURATION_OPTIONS[selectedDuration];
      if (
        contact.CALL_DURATION_SECONDS < durationOption.lowerBound ||
        contact.CALL_DURATION_SECONDS > durationOption.upperBound
      ) {
        return false;
      }
    }

    if (selectedTimeOfDay !== 0 && contact.TIME) {
      const timeOfDayOption = TIME_OF_DAY_OPTIONS[selectedTimeOfDay];
      const timeInHourFormat = formatTimeAs24Hour(contact.TIME);
      if (
        timeInHourFormat < timeOfDayOption.lowerBound ||
        timeInHourFormat > timeOfDayOption.upperBound
      ) {
        return false;
      }
    }

    if (selectedOwnership !== Ownership.ALL && contact.OWNERSHIP_TYPE) {
      if (selectedOwnership !== contact.OWNERSHIP_TYPE) return false;
    }

    // Search term filter (searches COMM_ID)
    if (
      searchTerm &&
      !contact.COMM_ID.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }

    return true;
  });
};

/**
 * Converts an ISO timestamp string to 24-hour time format (HH:mm:ss)
 * @param isoTimestamp - ISO 8601 timestamp string
 * @returns Formatted time string in 24-hour format or '--:--:--' if invalid
 */
export const formatTimeAs24Hour = (isoTimestamp: string): string => {
  try {
    const momentDate = moment(isoTimestamp);

    if (!momentDate.isValid()) {
      console.warn(`Invalid timestamp provided: ${isoTimestamp}`);
      return '--:--:--';
    }

    return momentDate.format('HH:mm:ss');
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '--:--:--';
  }
};

export const sortContacts = (
  contacts: PastContact[],
  sortColumn: PastContactsTableSortColumn,
  sortDirection: SortDirection
): PastContact[] => {
  if (!contacts || !Array.isArray(contacts)) {
    return [];
  }

  return [...contacts].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortColumn) {
      case PastContactsTableSortColumn.TIME:
        aValue = new Date(a.TIME).getTime();
        bValue = new Date(b.TIME).getTime();
        break;
      case PastContactsTableSortColumn.OWNERSHIP_TYPE:
        aValue = a.OWNERSHIP_TYPE.toLowerCase();
        bValue = b.OWNERSHIP_TYPE.toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === SortDirection.ASCENDING ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === SortDirection.ASCENDING ? 1 : -1;
    }
    return 0;
  });
};
